import React, { useState } from "react";
import {
  Box,
  Collapse,
  Container,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { ExpandLessOutlined, ExpandMoreOutlined } from "@mui/icons-material";
import { useTenantContactInfo } from "../../hooks/useTenantContactInfo";

const FOOTER_VERSION = "1.0";
const SAMI_URL = "https://www.mysami.io";

const formatFooterDate = (date: Date): string =>
  date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

function formatWebsiteHref(website: string): string {
  const trimmed = website.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

const Footer: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const today = formatFooterDate(new Date());
  const { contact } = useTenantContactInfo();

  const hasOrgInfo =
    contact &&
    (contact.companyName ||
      contact.website ||
      contact.phone ||
      contact.contactEmail ||
      contact.address);

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        borderTop: "1px solid",
        borderColor: "divider",
        mt: "auto",
        flexShrink: 0,
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 1,
            minHeight: 40,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Link
              href={SAMI_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                color: "text.secondary",
                fontSize: "0.8125rem",
                fontWeight: 500,
                whiteSpace: "nowrap",
                "&:hover": { color: "primary.main" },
              }}
            >
              Powered by SAMi
            </Link>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              ·
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              v{FOOTER_VERSION}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse footer" : "Expand footer"}
            sx={{ color: "text.secondary" }}
          >
            {expanded ? (
              <ExpandLessOutlined fontSize="small" />
            ) : (
              <ExpandMoreOutlined fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "center", md: "flex-start" },
              justifyContent: "space-between",
              gap: 2,
              pb: 2,
              pt: 0.5,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            {hasOrgInfo && (
              <Box sx={{ flex: 1 }}>
                {contact?.companyName && (
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {contact.companyName}
                  </Typography>
                )}
                {contact?.address && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {contact.address}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  {contact?.website && (
                    <Link
                      href={formatWebsiteHref(contact.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      color="text.secondary"
                      sx={{ fontSize: "0.8125rem" }}
                    >
                      {contact.website}
                    </Link>
                  )}
                  {contact?.phone && (
                    <Typography variant="caption" color="text.secondary">
                      {contact.phone}
                    </Typography>
                  )}
                  {contact?.contactEmail && (
                    <Link
                      href={`mailto:${contact.contactEmail}`}
                      underline="hover"
                      color="text.secondary"
                      sx={{ fontSize: "0.8125rem" }}
                    >
                      {contact.contactEmail}
                    </Link>
                  )}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.25,
                minWidth: { md: 180 },
              }}
            >
              <Link
                href={SAMI_URL}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&:hover": { color: "primary.main" },
                }}
              >
                www.mysami.io
              </Link>
              <Typography variant="caption" color="text.secondary">
                {today}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Container>
    </Box>
  );
};

export default Footer;
